import React from 'react'

import i18next, { type i18n as I18nInstance } from 'i18next'
import {
  I18nextProvider,
  initReactI18next,
  useTranslation,
} from 'react-i18next'

import {
  BUILTIN_DICTS,
  BUILTIN_NAMESPACES,
  cloneTranslationDict,
  type TranslationDict,
  type TranslationNamespaceDict,
} from '@runtime/core/i18n/locales/builtinLocales'
import { useAppStore } from '@runtime/core/store/appStore'
import {
  SUPPORTED_LOCALES,
  localeLanguageTag,
} from '@runtime/core/i18n/localeConfig'

export const I18N_DEFAULT = 'fr'

export const I18N_SUPPORTED = SUPPORTED_LOCALES
export type { SupportedLocale } from '@runtime/core/i18n/localeConfig'

export type I18nVars = Record<
  string,
  string | number | boolean | null | undefined
>

export type I18nT = (key: string, vars?: I18nVars) => string

export const I18N_RTL = new Set(['ar', 'he', 'fa', 'ur'])

const AVAILABLE_LOCALES = new Set<string>(Object.keys(BUILTIN_DICTS))
const AVAILABLE_NAMESPACES = new Set<string>(BUILTIN_NAMESPACES)

export function getAvailableLocales() {
  return Array.from(new Set([...SUPPORTED_LOCALES, ...AVAILABLE_LOCALES]))
}

function getAvailableNamespaces() {
  return Array.from(AVAILABLE_NAMESPACES)
}

function nearestSupportedLocale(code: unknown) {
  const supported = getAvailableLocales()
  if (!code) return null
  const c = String(code).toLowerCase()
  if (supported.includes(c)) return c
  const c2 = c.slice(0, 2)
  return supported.includes(c2) ? c2 : null
}

function pickInitialLocale() {
  // Prefer Zustand store (single source of truth).
  try {
    const storeLocale = useAppStore.getState().locale
    const picked = nearestSupportedLocale(storeLocale)
    if (picked) return picked
  } catch {
    // ignore
  }

  const nav =
    (typeof navigator !== 'undefined' && navigator.language) || I18N_DEFAULT
  return nearestSupportedLocale(nav) || I18N_DEFAULT
}

function buildResources() {
  const out: Record<string, TranslationNamespaceDict> = {}
  for (const [locale, dict] of Object.entries(BUILTIN_DICTS)) {
    out[locale] = Object.fromEntries(
      Object.entries(dict).map(([namespace, bundle]) => [
        namespace,
        cloneTranslationDict(bundle),
      ]),
    )
  }
  return out
}

function mergeLocaleNamespaces(locale: string, dict: Record<string, unknown>) {
  for (const [namespace, bundle] of Object.entries(dict)) {
    if (!bundle || typeof bundle !== 'object' || Array.isArray(bundle)) continue

    AVAILABLE_NAMESPACES.add(namespace)
    i18n.addResourceBundle(
      locale,
      namespace,
      cloneTranslationDict(bundle as TranslationDict),
      true,
      true,
    )
  }

  i18n.options.ns = getAvailableNamespaces()
}

export const i18n: I18nInstance = i18next.createInstance()

if (!i18n.isInitialized) {
  i18n.use(initReactI18next).init({
    initImmediate: false,
    resources: buildResources(),
    lng: pickInitialLocale(),
    ns: getAvailableNamespaces(),
    defaultNS: 'common',
    fallbackLng: {
      default: ['en', I18N_DEFAULT],
    },
    supportedLngs: getAvailableLocales(),
    nsSeparator: '.',
    interpolation: {
      escapeValue: false,
      prefix: '{',
      suffix: '}',
    },
    returnNull: false,
    returnEmptyString: false,
    react: {
      useSuspense: false,
      // re-render when resources are added/removed at runtime (external i18n)
      bindI18n: 'languageChanged',
      bindI18nStore: 'added removed',
    },
  })
}

type I18nProviderProps = {
  children: React.ReactNode
}

export function I18nProvider({ children }: I18nProviderProps) {
  const [, forceRender] = React.useState(0)

  const storeLocale = useAppStore((s) => s.locale)

  // Keep lang/dir synced
  React.useEffect(() => {
    const apply = (locale: string) => {
      document.documentElement.lang = localeLanguageTag(locale)
      document.documentElement.dir = I18N_RTL.has(locale) ? 'rtl' : 'ltr'
    }

    const onLang = (locale: string) => apply(locale)
    apply(i18n.language || I18N_DEFAULT)
    i18n.on('languageChanged', onLang)

    return () => {
      i18n.off('languageChanged', onLang)
    }
  }, [])

  // Keep i18next language synced with Zustand store.
  React.useEffect(() => {
    const picked = nearestSupportedLocale(storeLocale) || I18N_DEFAULT
    if (picked && i18n.language !== picked) {
      i18n.changeLanguage(picked)
    }
  }, [storeLocale])

  // Global helpers (kept for backward compatibility with legacy code)
  React.useEffect(() => {
    window.__i18nSetLocale = (locale: unknown) => {
      const picked = nearestSupportedLocale(locale) || I18N_DEFAULT
      useAppStore.getState().setLocale(picked)
    }

    window.__i18nBump = () => {
      forceRender((x) => x + 1)
    }

    return () => {
      try {
        delete window.__i18nSetLocale
      } catch {
        // ignore
      }
      try {
        delete window.__i18nBump
      } catch {
        // ignore
      }
    }
  }, [])

  // Optional external i18n loader (assets/i18n/*.json)
  React.useEffect(() => {
    if (typeof window === 'undefined') return
    if (window.location.protocol.indexOf('http') !== 0) return

    let cancelled = false

    async function loadExternalI18n() {
      try {
        const idxRes = await fetch('/assets/i18n/index.json')
        const idxType = idxRes.headers.get('content-type') || ''
        const idx =
          idxRes.ok && idxType.includes('application/json')
            ? await idxRes.json()
            : null
        const langs =
          idx && Array.isArray(idx.langs) && idx.langs.length
            ? idx.langs
            : getAvailableLocales()

        for (const rawLocale of langs) {
          const locale = String(rawLocale || '').toLowerCase()
          try {
            const r = await fetch('/assets/i18n/' + locale + '.json')
            const rType = r.headers.get('content-type') || ''
            if (r.ok && rType.includes('application/json')) {
              const dict = await r.json()
              if (cancelled) return

              // Track any extra locales provided by external packs.
              try {
                AVAILABLE_LOCALES.add(locale)
                // keep i18next's supportedLngs in sync
                i18n.options.supportedLngs = getAvailableLocales()
              } catch {
                // ignore
              }

              // Merge/overwrite i18next resources namespace by namespace.
              if (dict && typeof dict === 'object' && !Array.isArray(dict)) {
                mergeLocaleNamespaces(locale, dict as Record<string, unknown>)
              }
            }
          } catch {
            // ignore per-lang errors
          }
        }

        if (cancelled) return

        // Re-apply store locale if it exists in newly loaded resources
        try {
          const picked = nearestSupportedLocale(useAppStore.getState().locale)
          if (picked) {
            useAppStore.getState().setLocale(picked)
          }
        } catch {
          // ignore
        }

        // Force a render so language pickers can include any newly loaded locales.
        forceRender((x) => x + 1)
      } catch {
        // ignore external i18n failures (missing files, non-JSON responses, etc.)
      }
    }

    if (
      document.readyState === 'complete' ||
      document.readyState === 'interactive'
    ) {
      loadExternalI18n()
    } else {
      const onReady = () => loadExternalI18n()
      window.addEventListener('DOMContentLoaded', onReady, { once: true })
      return () => window.removeEventListener('DOMContentLoaded', onReady)
    }

    return () => {
      cancelled = true
    }
  }, [])

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
}

export function useI18n() {
  const { i18n: inst } = useTranslation()

  const storeLocale = useAppStore((s) => s.locale)
  const setStoreLocale = useAppStore((s) => s.setLocale)

  const locale = storeLocale || inst.language || I18N_DEFAULT

  const t = React.useCallback(
    (key: string, vars?: Record<string, unknown>) => inst.t(key, vars),
    [inst],
  )

  const setLocale = React.useCallback(
    (next: string) => {
      const picked = nearestSupportedLocale(next) || I18N_DEFAULT
      setStoreLocale(picked)
    },
    [setStoreLocale],
  )

  const tNode = React.useCallback(
    (
      key: string,
      tokens?: Record<string, React.ReactNode | (() => React.ReactNode)>,
    ): React.ReactNode => {
      const s = t(key)
      if (!tokens) return s

      const parts: React.ReactNode[] = []
      let last = 0
      const re = /\{(\w+)\}/g
      let m
      while ((m = re.exec(String(s)))) {
        if (m.index > last) parts.push(String(s).slice(last, m.index))
        const name = m[1]
        const node = tokens[name]
        parts.push(
          typeof node === 'function' ? node() : (node ?? '{' + name + '}'),
        )
        last = re.lastIndex
      }
      if (last < String(s).length) parts.push(String(s).slice(last))

      return React.createElement(React.Fragment, null, ...parts)
    },
    [t],
  )

  return { locale, setLocale, t, tNode }
}
